import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Mail, Plus, X, Send } from "lucide-react";

interface FriendEmailFormProps {
  seats: number;
  ticketId?: string;
  eventId: string;
  onDone: () => void;
}

const FriendEmailForm = ({ seats, ticketId, eventId, onDone }: FriendEmailFormProps) => {
  const [emails, setEmails] = useState<string[]>([""]);
  const [isSending, setIsSending] = useState(false);

  const addEmailField = () => {
    if (emails.length < seats) {
      setEmails([...emails, ""]);
    }
  };

  const removeEmailField = (index: number) => {
    if (emails.length > 1) {
      setEmails(emails.filter((_, i) => i !== index));
    }
  };

  const updateEmail = (index: number, value: string) => {
    const updated = [...emails];
    updated[index] = value;
    setEmails(updated);
  };

  const handleSend = async () => {
    const validEmails = emails.filter((e) => e.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim()));
    if (validEmails.length === 0) {
      toast({ title: "Please enter at least one valid email", variant: "destructive" });
      return;
    }

    setIsSending(true);
    try {
      // Send emails and store in guest_ticket_emails
      const promises = validEmails.map(async (email) => {
        // Send simplified event info to friend
        await supabase.functions.invoke("send-ticket-confirmation", {
          body: { ticketId, guestEmail: email.trim(), eventId },
        });

        // Store in guest_ticket_emails if we have a ticketId
        if (ticketId) {
          await supabase.from("guest_ticket_emails").insert({
            ticket_id: ticketId,
            email: email.trim(),
            sent_at: new Date().toISOString(),
          });
        }
      });

      await Promise.all(promises);

      toast({
        title: "Event details sent!",
        description: `Sent to ${validEmails.length} friend${validEmails.length > 1 ? "s" : ""}.`,
      });
      onDone();
    } catch (err) {
      console.error("Error sending friend emails:", err);
      toast({
        title: "Failed to send",
        description: "Please try again or skip for now.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-center">
        <Mail className="h-8 w-8 text-primary mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">
          Enter your friend{seats > 1 ? "s'" : "'s"} email so they get event details
        </p>
      </div>

      <div className="space-y-2">
        {emails.map((email, index) => (
          <div key={index} className="flex gap-2">
            <Input
              type="email"
              placeholder={`Friend${emails.length > 1 ? ` ${index + 1}` : ""}'s email`}
              value={email}
              onChange={(e) => updateEmail(index, e.target.value)}
              disabled={isSending}
            />
            {emails.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeEmailField(index)}
                disabled={isSending}
                className="shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {emails.length < seats && (
        <Button
          variant="ghost"
          size="sm"
          onClick={addEmailField}
          disabled={isSending}
          className="gap-1 text-muted-foreground"
        >
          <Plus className="h-3 w-3" />
          Add another email
        </Button>
      )}

      <div className="flex gap-3">
        <Button onClick={handleSend} disabled={isSending} className="flex-1 gap-2">
          <Send className="h-4 w-4" />
          {isSending ? "Sending..." : "Send Event Details"}
        </Button>
        <Button variant="ghost" onClick={onDone} disabled={isSending}>
          Skip
        </Button>
      </div>
    </div>
  );
};

export default FriendEmailForm;
